import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import apiService from '../../services/api';

/**
 * AccentSelector — Preset accent selection + freeform guidance input.
 * Used in Admin platform settings, Therapist voice settings, and the
 * scenario editor (where it can also offer LLM-generated guidance).
 *
 * Props:
 *   value, onChange — accent preset id (or 'custom' / 'default'/null)
 *   customGuidance, onCustomGuidanceChange — freeform guidance text
 *   onGenerate (optional) — async callback returning guidance text;
 *     when provided, a "Generate from persona" button appears that
 *     auto-switches the preset to 'custom' and populates the field.
 */
const AccentSelector = ({
  value,
  onChange,
  customGuidance,
  onCustomGuidanceChange,
  token,
  onGenerate,
}) => {
  const [presets, setPresets] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(null);

  useEffect(() => {
    const fetchPresets = async () => {
      try {
        const data = await apiService.getAccentPresets(token);
        setPresets(data.presets || []);
      } catch (err) {
        console.error('Failed to fetch accent presets:', err);
      }
    };
    if (token) fetchPresets();
  }, [token]);

  const selectedPreset = presets.find(p => p.id === value);
  const isCustom = value === 'custom';

  const handleGenerate = async () => {
    if (!onGenerate || generating) return;
    if (customGuidance && customGuidance.trim() && !window.confirm(
      'This will replace your current voice guidance with a freshly generated draft. Continue?'
    )) {
      return;
    }
    setGenerating(true);
    setGenerateError(null);
    try {
      const guidance = await onGenerate();
      if (guidance) {
        // Switch the preset to 'custom' so the resolver passes our
        // generated paragraph to Gemini Live verbatim, then populate the
        // textarea so the author can fine-tune.
        if (value !== 'custom') onChange('custom');
        onCustomGuidanceChange(guidance);
      }
    } catch (err) {
      setGenerateError(err?.message || 'Failed to generate voice guidance');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Box>
      <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
        <InputLabel>Accent / Regional Style</InputLabel>
        <Select
          value={value || 'default'}
          onChange={(e) => onChange(e.target.value === 'default' ? null : e.target.value)}
          label="Accent / Regional Style"
        >
          {presets.map(preset => (
            <MenuItem key={preset.id} value={preset.id}>
              {preset.label}
            </MenuItem>
          ))}
          <MenuItem value="custom">Custom (freeform guidance)</MenuItem>
        </Select>
      </FormControl>

      {selectedPreset?.guidance && !isCustom && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontStyle: 'italic' }}>
          Live session guidance: "{selectedPreset.guidance}"
        </Typography>
      )}

      {isCustom && (
        <Typography variant="caption" color="info.main" sx={{ display: 'block', mb: 0.5 }}>
          Note: Custom accents apply to live voice sessions only. Voice previews play with the base voice.
        </Typography>
      )}

      <Collapse in={isCustom}>
        <TextField
          fullWidth
          multiline
          rows={4}
          size="small"
          label="Custom accent/style guidance (used in live voice sessions)"
          placeholder='e.g. "Speak with a warm Dublin Irish accent. Be friendly and personable."'
          value={customGuidance || ''}
          onChange={(e) => onCustomGuidanceChange(e.target.value)}
          helperText="This text is added to the AI's system prompt during live voice conversations"
          sx={{ mt: 1 }}
        />
      </Collapse>

      {!isCustom && selectedPreset?.id !== 'default' && (
        <TextField
          fullWidth
          multiline
          rows={2}
          size="small"
          label="Additional voice style guidance (optional)"
          placeholder="Add extra instructions beyond the preset, e.g. specific phrases or tone adjustments"
          value={customGuidance || ''}
          onChange={(e) => onCustomGuidanceChange(e.target.value)}
          helperText="Combined with the preset guidance above during live voice sessions"
          sx={{ mt: 1.5 }}
        />
      )}

      {/* Generate-from-persona action — only when a callback is provided */}
      {onGenerate && (
        <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={generating ? <CircularProgress size={14} /> : <AutoAwesomeIcon />}
            onClick={handleGenerate}
            disabled={generating}
          >
            {generating ? 'Drafting…' : 'Generate from persona'}
          </Button>
          <Typography variant="caption" color="text.secondary">
            Drafts a stage-direction-style paragraph (gender, accent, communication style, sample phrases)
            from the linked persona. Switches the accent above to "Custom".
          </Typography>
          {generateError && (
            <Typography variant="caption" color="error.main" sx={{ width: '100%' }}>
              {generateError}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default AccentSelector;
